import { ref } from 'vue'
import { useNotificationCenter } from '~/composables/useNotificationCenter'
import { useLocale } from '~/composables/useLocale'

const recentKeys = new Map<string, number>()
const RECENT_KEY_TTL_MS = 60 * 60 * 1000
const RECENT_KEY_MAX = 150

function pruneRecentKeys() {
  const now = Date.now()
  for (const [key, ts] of recentKeys) {
    if (now - ts > RECENT_KEY_TTL_MS) recentKeys.delete(key)
  }
  if (recentKeys.size <= RECENT_KEY_MAX) return
  const sorted = [...recentKeys.entries()].sort((a, b) => a[1] - b[1])
  for (let i = 0; i < sorted.length - RECENT_KEY_MAX; i++) {
    recentKeys.delete(sorted[i]![0])
  }
}

export function useProgressionMoments() {
  const { notify } = useNotificationCenter()
  const { t } = useLocale()
  const lastZoneDpsMilestone = ref(0)

  function notifyOnce(key: string, messageFr: string, messageEn: string, icon = '✨') {
    pruneRecentKeys()
    if (recentKeys.has(key)) return
    recentKeys.set(key, Date.now())
    notify('progression', t(messageFr, messageEn), { icon, coalesceKey: 'progression' })
  }

  function onFirstZoneBoss(gen: number, zone: number) {
    notifyOnce(
      `boss_first_g${gen}z${zone}`,
      `Premier boss de la zone ${zone} vaincu !`,
      `First boss of zone ${zone} defeated!`,
      '🏆',
    )
  }

  function onDpsMilestonePct(gen: number, zone: number, pct: number) {
    const key = `dps_milestone_g${gen}z${zone}_${pct}`
    notifyOnce(
      key,
      `Puissance +${pct} % sur cette zone`,
      `Power +${pct}% on this zone`,
      '⚡',
    )
  }

  function onAscensionUpgradeBought(upgradeLabel: string) {
    notifyOnce(`asc_upgrade_${upgradeLabel}`, `Amélioration : ${upgradeLabel}`, `Upgrade: ${upgradeLabel}`, '👑')
  }

  function onCapstoneDefeated() {
    notifyOnce(
      'capstone_red',
      'Red vaincu — bonus ascension ×1,18 disponible.',
      'Red defeated — ×1.18 ascension bonus available.',
      '🔴',
    )
  }

  return {
    lastZoneDpsMilestone,
    onFirstZoneBoss,
    onDpsMilestonePct,
    onAscensionUpgradeBought,
    onCapstoneDefeated,
  }
}
